import axios from 'axios';
import * as types from '@/store/mutation-types';
import {CommissionPartnerService} from '@/services/commission-partner.service';
import {CartService} from '@/services/cart.service';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {Cart} from '@/types';
import {companyCartQueryParams} from '@/utils/appHost';
import dayjs from 'dayjs';
import {d} from '@/utils/consoleHelper.ts';
import {isStorefrontPreview} from '@/utils/storefrontPreview';

export interface CartState {
    loading: Record<string, boolean>;
    cart: Cart;
}

const state: CartState = {
    loading: {
        cart: false,
        fetchCart: false,
    },
    cart: {
        loaded: false,
        uuid: CartService.getCartUuid() || '',
        l_id: null,
        trip_start: null,
        trip_end: null,
        b_id: null,
        checkout_url: null,
        commission_partner_id: null,
        company_uuid: '',
        complete: 0,
        coupon_discount: null,
        coupon_id: null,
        created_at: '',
        deposit: null,
        grand_total: null,
        price: null,
        tax: null,
        total_due: null,
        updated_at: '',
        website_url: null,
    },
};

const getters = {
    loading: (state: CartState) => state.loading,
    cart: (state: CartState) => state.cart,
};

const mutations = {
    [types.SET_CART_LOADING](state: CartState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_CART_L_ID](state: CartState, l_id: string) {
        state.cart.l_id = l_id;
    },
    [types.SET_CART](state: CartState, cart: Cart) {
        CartService.saveCartUuid(cart.uuid);
        state.cart = cart;
        state.cart.loaded = true;
    },
};

const sanitizeCartId = (uuid?: string | null): string =>
    uuid?.replace(/[^a-zA-Z0-9-]/g, '') ?? '';

const axiosResponseStatus = (error: unknown): number | undefined =>
    axios.isAxiosError(error) ? error.response?.status : undefined;

const isMissingCartError = (error: unknown): boolean =>
    axiosResponseStatus(error) === 404;

const reloadOnMissingCart = (): void => {
    CartService.removeCartUuid();
    window.location.reload();
};

const isExpectedClientError = (error: unknown, statuses: number[]): boolean => {
    const status = axiosResponseStatus(error);

    return typeof status === 'number' && statuses.includes(status);
};

const actions = {
    async createCart({commit}, payload: Record<string, unknown> = {}) {
        const params = companyCartQueryParams();
        const partnerUuid = CommissionPartnerService.getPartnerUuid();
        const body: Record<string, unknown> = partnerUuid
            ? {...payload, cp: partnerUuid}
            : {...payload};

        if (isStorefrontPreview()) {
            body.preview = true;
        }

        try {
            const query = params.toString();
            const responseData = await performApiRequest(
                commit,
                types.SET_CART_LOADING,
                'createCart',
                () => axios.post(`/v1/carts${query ? `?${query}` : ''}`, body),
            );
            commit(types.SET_CART, responseData.cart);

            return responseData;
        } catch (e) {
            d.error('Error creating cart:', e);
        }
    },

    async fetchCart({commit}, cartId: string) {
        try {
            const responseData = await performApiRequest(
                commit,
                types.SET_CART_LOADING,
                'fetchCart',
                () => axios.get(`/v1/carts/${cartId}`),
            );
            commit(types.SET_CART, responseData.cart);

            return responseData.cart;
        } catch (error) {
            if (isMissingCartError(error)) {
                reloadOnMissingCart();
            }
        }
    },

    async setPeriod({state, commit}, dates: { start: string, end: string }) {
        const cartId = sanitizeCartId(state.cart?.uuid);

        // Empty string is typeof 'string' — without this guard we POST /carts//set-period (405).
        if (!cartId) {
            return;
        }

        const start = dayjs(dates.start);
        const end = dayjs(dates.end);

        // Match SetPeriodRequest: end must be after start. Mid-selection ranges are often equal.
        if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
            return;
        }

        try {
            const responseData = await performApiRequest(
                commit,
                types.SET_CART_LOADING,
                'setPeriod',
                () => axios.post(`/v1/carts/${cartId}/set-period`, {
                    start: start.format('YYYY-MM-DD HH:mm:00'),
                    end: end.format('YYYY-MM-DD HH:mm:00'),
                }),
            );
            commit(types.SET_CART, responseData.cart);

            return responseData.cart;
        } catch (error) {
            if (isMissingCartError(error)) {
                reloadOnMissingCart();
                return;
            }

            // Expected client errors (validation, empty-cart race) — don't bubble to Sentry.
            if (isExpectedClientError(error, [422, 405])) {
                return;
            }

            throw error;
        }
    },

    /**
     * The conversion beacon. The server answers 422 on purpose when the visit
     * is not a conversion (a host opening the status page, a renter reloading
     * it after the cart was cleared), so that answer is silence, not an error:
     * it goes through axios directly rather than the helper that logs every
     * failure to the console and Sentry.
     */
    async report({commit}, data = {}) {
        // Preview carts are ignored server-side too; skip the request entirely.
        if (isStorefrontPreview()) {
            return;
        }

        commit(types.SET_CART_LOADING, {subject: 'report', loading: true});

        try {
            await axios.post('/v1/report', data);
        } catch (error) {
            if (isExpectedClientError(error, [422])) {
                return;
            }

            throw error;
        } finally {
            commit(types.SET_CART_LOADING, {subject: 'report', loading: false});
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
